import { contentOrder } from './siteSpeakersApi';
const ROOT = '/api/site/v1/courses';
export const COURSE_CATEGORIES = [
  { id: 'courses', label: 'Courses' },
  { id: 'training', label: 'Training' },
  { id: 'orientation', label: 'Orientation' },
  { id: 'webinars', label: 'Webinars' },
];
const categories = new Set(COURSE_CATEGORIES.map(({ id }) => id));
const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const unavailable = () => new Error('Courses could not connect to the website. Please try again later.');

export async function requestPublicCourse(path = '', signal) {
  if (!/^(?:\?limit=100(?:&after=[A-Za-z0-9_-]{1,128})?|\/hero|\/featured|\/by-slug\/[a-z0-9]+(?:-[a-z0-9]+)*)?$/.test(path)) {
    throw new Error('Invalid course API path.');
  }
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal?.addEventListener('abort', cancel, { once: true });
  if (signal?.aborted) controller.abort();
  const timer = setTimeout(cancel, 20000);
  try {
    const response = await fetch(ROOT + path, {
      method: 'GET', credentials: 'omit', cache: 'no-store', redirect: 'error',
      referrerPolicy: 'no-referrer', signal: controller.signal,
    });
    const result = await response.json().catch(() => null);
    if (!response.ok || !result || !Object.prototype.hasOwnProperty.call(result, 'data')) {
      const error = unavailable();
      error.status = response.status;
      error.code = result?.error?.code;
      throw error;
    }
    return result.data;
  } catch (error) {
    if (error.status || signal?.aborted) throw error;
    throw unavailable();
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', cancel);
  }
}

function publicCourse(record) {
  if (!record || record.status !== 'published' || !/^[A-Za-z0-9_-]{1,128}$/.test(record.id || '') ||
      typeof record.title !== 'string' || !record.title.trim() || typeof record.slug !== 'string' ||
      record.slug.length > 150 || !slugPattern.test(record.slug) || !categories.has(record.category)) return null;
  // Enrollment is enabled only by the website's approved delivery/offer projection.
  const enabled = record.isDemo !== true && record.enrollmentEnabled === true && ['free','paid'].includes(record.offer?.mode) && record.offer.currency === 'USD';
  return { ...record, enrollmentEnabled: enabled, paymentRequired: enabled && record.offer.mode === 'paid', paymentAmount: null };
}

export async function listPublicCourses(signal) {
  const records = new Map(), cursors = new Set();
  let after = null;
  do {
    const page = await requestPublicCourse(`?limit=100${after ? `&after=${after}` : ''}`, signal);
    if (!page || !Array.isArray(page.items) || (page.next !== null && !/^[A-Za-z0-9_-]{1,128}$/.test(page.next || ''))) throw unavailable();
    page.items.forEach((value) => { const record = publicCourse(value); if (record) records.set(record.id, record); });
    after = page.next;
    if (after && cursors.has(after)) throw unavailable();
    if (after) cursors.add(after);
  } while (after);
  return [...records.values()].sort(contentOrder());
}

export async function getPublicCourseBySlug(slug, signal) {
  const value = String(slug || '').trim().toLowerCase();
  if (!slugPattern.test(value) || value.length > 150) return null;
  try {
    const record = publicCourse(await requestPublicCourse(`/by-slug/${value}`, signal));
    if (!record || record.slug !== value) throw unavailable();
    return record;
  } catch (error) {
    if (error.status === 404 && error.code === 'not_found') return null;
    throw error;
  }
}
